import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CircleCheck, Sparkles, TrendingUp } from "lucide-react";
import { Shell } from "@/components/control-tower/Layout";
import { PerspectiveHero } from "@/components/control-tower/PerspectiveHero";
import { OverviewSituationCards } from "@/components/control-tower/perspectives/OverviewSituationCards";
import { InventoryMap } from "@/components/control-tower/InventoryMap";
import { StrategyPortfolio } from "@/components/control-tower/perspectives/StrategyPortfolio";
import { StrategyBuildingSection } from "@/components/control-tower/perspectives/building/StrategyBuildingSection";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { useDashboardStats } from "@/lib/api/useDashboardStats";
import { useFilteredBuildings } from "@/lib/inventory-scope";
import type { Building } from "@/lib/api/building";
import type { OverviewBucket } from "@/lib/overview-lens";

export const Route = createFileRoute("/savings-potential")({
  component: StrategyPage,
});

// The four Säästöpotentiaali situation-card buckets — driven by the same live `Building` fields
// (status, active_optimizations) `useDashboardStats` already derives from `GET /buildings`, not a
// second fetch or fabricated literal (#1715: the old `SituationCards` + `role-content.ts` tables
// rendered static €/% mocks as if live).
type StrategyFacetKey = "needs-action" | "potential" | "active" | "optimized";

const STRATEGY_BUCKET_DEFS: {
  key: StrategyFacetKey;
  label: string;
  icon: OverviewBucket["icon"];
  predicate: (building: Building) => boolean;
}[] = [
  {
    key: "needs-action",
    label: "Vaatii toimenpiteitä",
    icon: AlertTriangle,
    predicate: (b) => b.status === "alert",
  },
  {
    key: "potential",
    label: "Potentiaalia tunnistettu",
    icon: TrendingUp,
    predicate: (b) => b.status === "warn",
  },
  {
    key: "active",
    label: "Aktiivinen optimointi",
    icon: Sparkles,
    predicate: (b) => b.active_optimizations > 0,
  },
  {
    key: "optimized",
    label: "Optimoitu",
    icon: CircleCheck,
    predicate: (b) => b.status === "ok",
  },
];

const STRATEGY_SITUATION_COPY = {
  situationCardSubtitle: (count: number) =>
    count === 1 ? "1 kiinteistössä" : `${count} kiinteistössä`,
  situationCardAllDoneLabel: "Ei kohteita tässä ryhmässä",
  emptyScopeHint: "Ei kiinteistöjä valitulla rajauksella",
};

// eslint-disable-next-line react-refresh/only-export-components -- exported for savings-potential.test.ts
export function strategyBuckets(buildings: Building[]): OverviewBucket[] {
  return STRATEGY_BUCKET_DEFS.map(({ key, label, icon, predicate }) => {
    const matched = buildings.filter(predicate);
    return {
      key,
      label,
      icon,
      tone: "warn",
      count: matched.length,
      buildings: matched.map((b) => ({
        id: b.id,
        name: b.name,
        city: b.city,
      })),
    };
  });
}

// eslint-disable-next-line react-refresh/only-export-components -- exported for savings-potential.test.ts
export function facetPredicate(
  key: string | null,
): (building: Building) => boolean {
  const def = STRATEGY_BUCKET_DEFS.find((d) => d.key === key);
  return def?.predicate ?? (() => true);
}

// Strategia perspective — scope-adaptive: one building vs. the portfolio (kanta/salkku).
function StrategyPage() {
  const { buildingId, groupId, filter } = useInventoryFilter();
  const state = useDashboardStats();
  const buildings = useFilteredBuildings(
    state.status === "ok" ? state.buildings : [],
  );
  const [facetKey, setFacetKey] = useState<string | null>(null);

  // Same facet-reset discipline as PerspectiveOverview: a stale facet must never outlive the
  // scope it was computed against.
  const scopeResetKey = [
    groupId ?? "",
    filter.city,
    filter.type,
    filter.status,
    buildings
      .map((b) => b.id)
      .sort()
      .join(","),
  ].join("|");
  useEffect(() => {
    setFacetKey(null);
  }, [scopeResetKey]);

  const buckets = useMemo(() => strategyBuckets(buildings), [buildings]);
  const narrowedBuildings = useMemo(
    () => buildings.filter(facetPredicate(facetKey)),
    [buildings, facetKey],
  );

  return (
    <Shell
      title="Säästöpotentiaali"
      subtitle="Suunta ja rajat · säästöpotentiaali"
    >
      <div className="space-y-6">
        <PerspectiveHero perspectiveId="savings-potential" />
        {buildingId ? (
          <StrategyBuildingSection
            buildingId={buildingId}
            buildings={buildings}
          />
        ) : (
          <>
            <OverviewSituationCards
              buckets={buckets}
              totalCount={buildings.length}
              loading={state.status === "loading"}
              copy={STRATEGY_SITUATION_COPY}
              selectedKey={facetKey}
              onSelect={(key) =>
                setFacetKey((current) => (current === key ? null : key))
              }
            />
            <StrategyPortfolio buildings={narrowedBuildings} />
            <InventoryMap />
          </>
        )}
      </div>
    </Shell>
  );
}
